import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Mail, Wrench, Image as ImageIcon, FileText, Building2,
  LogOut, ExternalLink, Menu, X, ChevronRight,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import LanguageSwitcher from "../../components/LanguageSwitcher";

const AdminLayout = () => {
  const { t } = useTranslation();
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-500">
          <span className="skeleton w-32 h-4 rounded" />
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const navItems = [
    { to: "/admin", label: t("admin.dashboard"), icon: LayoutDashboard, end: true, key: "dashboard" },
    { to: "/admin/inquiries", label: t("admin.inquiries"), icon: Mail, key: "inquiries" },
    { to: "/admin/services", label: t("admin.servicesAdmin"), icon: Wrench, key: "services" },
    { to: "/admin/gallery", label: t("admin.galleryAdmin"), icon: ImageIcon, key: "gallery" },
    { to: "/admin/clients", label: "Clients", icon: Building2, key: "clients" },
    { to: "/admin/content", label: t("admin.contentAdmin"), icon: FileText, key: "content" },
  ];

  // Crumbs
  const current = navItems.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to))) || navItems[0];

  const initials = user.email?.[0]?.toUpperCase() || "A";

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 ltr:left-0 rtl:right-0 w-64 bg-white border-r border-slate-200 flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full ltr:lg:translate-x-0 rtl:translate-x-full rtl:lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="px-5 py-4 flex items-center gap-2.5 border-b border-slate-200">
          <img src="/logo.png" alt="RTC logo" className="h-12 w-12 object-contain shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-900 text-sm leading-tight">RTC Admin</div>
            <div className="text-[9px] text-slate-500 uppercase tracking-wider truncate">Ragime Trading &amp; Contracting</div>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden text-slate-400 hover:text-slate-700" aria-label="Close sidebar">
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 admin-scroll overflow-y-auto">
          <p className="px-3 pt-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Manage</p>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-slate-900 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
              data-testid={`admin-nav-${item.key}`}
            >
              {({ isActive }) => (
                <>
                  <item.icon size={16} className={isActive ? "text-orange-400" : "text-slate-400 group-hover:text-slate-600"} />
                  <span className="flex-1">{item.label}</span>
                  {isActive && <ChevronRight size={14} className="text-orange-400" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-200 space-y-2">
          <Link
            to="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="admin-back-to-site"
          >
            <ExternalLink size={14} /> {t("admin.backToSite")}
          </Link>
          <div className="flex items-center gap-2.5 px-3 py-2.5 bg-slate-50 rounded-lg">
            <div className="w-8 h-8 bg-slate-900 text-white flex items-center justify-center font-bold rounded-full text-sm">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-slate-900 truncate" data-testid="admin-user-email">{user.email}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">{user.role || "admin"}</div>
            </div>
            <button
              onClick={handleLogout}
              className="text-slate-400 hover:text-red-600 p-1.5 rounded transition-colors"
              data-testid="admin-logout-btn"
              title={t("admin.logout")}
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="px-4 md:px-8 h-16 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
                onClick={() => setOpen(!open)}
                data-testid="admin-mobile-toggle"
                aria-label="Toggle sidebar"
              >
                <Menu size={20} />
              </button>
              <div className="flex items-center gap-2 text-sm text-slate-500 min-w-0">
                <Link to="/admin" className="hover:text-slate-900 hidden sm:inline">Admin</Link>
                <ChevronRight size={14} className="text-slate-300 hidden sm:inline" />
                <span className="font-semibold text-slate-900 truncate">{current.label}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher variant="minimal" />
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8 admin-scroll min-w-0 overflow-x-hidden">
          <div className="admin-fade-up">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
